import { useContext, useEffect } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';

export default function useReviewLeaveWarning(unsaved: boolean) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  useEffect(() => {
    if (!unsaved) return;
    const message = 'You have unsaved manager review changes. Leave this page?';
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    const click = (event: MouseEvent) => {
      const anchor = (event.target as Element)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (
        !anchor ||
        anchor.target === '_blank' ||
        anchor.download ||
        event.ctrlKey ||
        event.metaKey
      )
        return;
      const target = new URL(anchor.href);
      if (target.pathname === window.location.pathname && target.search === window.location.search)
        return;
      if (!window.confirm(message)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    const originalGo = navigator.go;
    let index = window.history.state?.idx;
    let restoring = false;
    const pop = (event: PopStateEvent) => {
      const next = event.state?.idx;
      if (restoring) {
        restoring = false;
        return;
      }
      if (typeof index === 'number' && typeof next === 'number' && !window.confirm(message)) {
        event.stopImmediatePropagation();
        restoring = true;
        window.history.go(index - next);
      } else {
        index = next;
      }
    };
    // browser history owns popstate; memory-router navigation uses go directly
    navigator.go = (delta) => {
      if (typeof window.history.state?.idx === 'number' || window.confirm(message))
        originalGo.call(navigator, delta);
    };
    window.addEventListener('beforeunload', beforeUnload);
    document.addEventListener('click', click, true);
    window.addEventListener('popstate', pop, true);
    return () => {
      navigator.go = originalGo;
      window.removeEventListener('beforeunload', beforeUnload);
      document.removeEventListener('click', click, true);
      window.removeEventListener('popstate', pop, true);
    };
  }, [navigator, unsaved]);
}
